import React, { useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import InputField from '~/components/atoms/InputField';
import SelectField from '~/components/atoms/SelectField';
import TextareaField from '~/components/atoms/TextareaField';
import CheckboxField from '~/components/atoms/CheckboxField';
import Button from '~/components/atoms/Button';
import useFormSubmit from '~/hooks/useFormSubmit';
import FadeInWhenVisible from '~/components/FadeInWhenVisible';
import ReCAPTCHA from 'react-google-recaptcha';

const VisuallyHiddenField = styled.div`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
  pointer-events: none;
`;

const validationSchema = Yup.object({
  firstname: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email address').required('Required'),
  mobilephone: Yup.string().required('Required'),
  how_we_can_help: Yup.string().required('Required'),
  your_message: Yup.string(),
  privacy: Yup.boolean().oneOf([true], 'This field must be checked'),
  formStartedAt: Yup.number().required(),
  recaptchaToken: Yup.string().required('Please verify that you are not a robot'),
});

export default function ContactForm({ isSubmitted, setIsSubmitted }) {
  const formRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const [isSending, setIsSending] = React.useState(false);

  return (
    <>
      <FadeInWhenVisible className="mb-20 md:mb-36">
        <div className="container sm:px-10">
          <div className="relative  mb-32 md:py-4 md:pb-8 sm:px-10 bg-white">
            {!isSubmitted ? (
              <Formik
                initialValues={{
                  firstname: '',
                  marketing_contact_type: 'Property Owner',
                  how_we_can_help: '',
                  email: '',
                  mobilephone: '',
                  your_message: '',
                  privacy: false,
                  formStartedAt: Date.now(),
                  recaptchaToken: '',
                }}
                validationSchema={validationSchema}
                onSubmit={async (values, { setFieldError }) => {
                  if (isSending || isSubmittingRef.current) return;

                  const formEl = formRef.current;

                  const botFieldValue =
                    formEl?.querySelector('input[name="website_url_check"]')?.value || '';
                  const spamChecked =
                    formEl?.querySelector('input[name="spamTrap"]')?.checked || false;

                  const secondsTaken = (Date.now() - values.formStartedAt) / 1000;
                  if (botFieldValue.trim() !== '') {
                    return;
                  }

                  if (spamChecked) {
                    return;
                  }

                  if (secondsTaken < 4) {
                    setFieldError(
                      'recaptchaToken',
                      'Submission was too fast. Please wait a few seconds and try again.'
                    );
                    return;
                  }

                  if (!values.recaptchaToken) {
                    setFieldError(
                      'recaptchaToken',
                      'Please verify that you are not a robot'
                    );
                    return;
                  }

                  isSubmittingRef.current = true;
                  setIsSending(true);
                  try {
                    const payload = {
                      firstname: values.firstname,
                      email: values.email,
                      mobilephone: values.mobilephone,
                      marketing_contact_type: values.marketing_contact_type,
                      how_we_can_help: values.how_we_can_help,
                      your_message: values.your_message?.trim() || 'No additional details provided.',
                    };

                    try {
                      const response = await fetch('/.netlify/functions/contact', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          ...payload,
                          recaptchaToken: values.recaptchaToken,
                          formStartedAt: values.formStartedAt,
                          botField: botFieldValue,
                          spamTrap: spamChecked,
                          pageUri: typeof window !== 'undefined' ? window.location.href : '',
                          pageName: typeof document !== 'undefined' ? document.title : '',
                        }),
                      });

                      const result = await response.json();

                      if (!response.ok) {
                        throw new Error(result?.message || 'Submit failed');
                      }

                      const eventId = `contact_form_submitted-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

                      // OpenAI Ads — browser pixel
                      if (typeof window !== 'undefined' && typeof window.oaiq === 'function') {
                        window.oaiq('measure', 'custom', { type: 'custom' }, { custom_event_name: 'contact_form_submitted', event_id: eventId });
                      }

                      // OpenAI Ads — server-side
                      try {
                        fetch('/.netlify/functions/openai-ads-event', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            custom_event_name: 'contact_form_submitted',
                            event_id: eventId,
                            source_url: window.location.href,
                          }),
                        });
                      } catch (e) {}

                      if (setIsSubmitted) {
                        setIsSubmitted(true);
                      }
                    } catch (error) {
                      // console.log('HubSpot submit error:', error);
                    }
                  } finally {
                    isSubmittingRef.current = false;
                    setIsSending(false);
                  }
                }}
              >
                {({ setFieldValue, errors, isValid, dirty, values, handleSubmit }) => (
                  <div
                    ref={formRef}
                    id="contact-form-main"
                    role="form"
                    className="grid max-w-md grid-cols-12 mx-auto gap-5 px-4"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                        e.preventDefault();
                        handleSubmit();
                      }
                    }}
                  >
                    <div className="col-span-12">
                      <div className="flex flex-wrap items-center text-h4">
                        I'm a{' '}
                        <div className="relative border-b-4 border-b-yellow ml-2.5 pr-8 heading-select">
                          {values.marketing_contact_type.replace('_', ' ')}
                          <select
                            className="absolute inset-0 z-10 block w-full pr-8 text-base bg-transparent outline-none opacity-0 cursor-pointer heading-select"
                            name="marketing_contact_type"
                            value={values.marketing_contact_type}
                            onChange={(e) => setFieldValue('marketing_contact_type', e.target.value)}
                          >
                            <option value="Property Owner">property owner</option>
                            <option value="Resident">resident</option>
                          </select>
                        </div>
                      </div>

                      <span
                        style={{
                          color: '#12203A',
                          fontFamily: 'Untitled Sans',
                          fontSize: '14px',
                          fontWeight: '400',
                          lineHeight: '24px',
                          marginTop: '3px',
                          marginBottom: '8px',
                          display: 'block',
                        }}
                      >
                        Select the option that best describes you.
                      </span>
                    </div>

                    <InputField
                      divclassname="col-span-12 mb-5"
                      name="firstname"
                      type="text"
                      label="Your full name"
                    />

                    <InputField
                      divclassname="col-span-12 mb-5"
                      name="email"
                      type="email"
                      label="Your email address"
                    />

                    <InputField
                      divclassname="col-span-12 mb-5"
                      name="mobilephone"
                      type="text"
                      label="Your phone number"
                    />

                    <SelectField
                      divclassname="col-span-12 mb-5"
                      name="how_we_can_help"
                      label="How can we help?"
                      options={[
                        { value: '', label: '' },
                        { value: 'rental_valuation', label: 'Rental valuation' },
                        { value: 'existing_booking', label: 'Existing booking' },
                        { value: 'general_enquiry', label: 'General enquiry' },
                      ]}
                    />

                    <CheckboxField
                      divclassname="col-span-12 mb-4"
                      name="privacy"
                      label={`By submitting this form, you accept our <a href="/privacy-policy">Privacy Policy</a>`}
                    />

                    <VisuallyHiddenField>
                      <label htmlFor="spamTrap"></label>
                      <input
                        id="spamTrap"
                        name="spamTrap"
                        type="checkbox"
                        defaultChecked={false}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </VisuallyHiddenField>

                    <VisuallyHiddenField>
                      <input
                        id="website_url_check"
                        name="website_url_check"
                        type="text"
                        autoComplete="new-password"
                        tabIndex="-1"
                        defaultValue=""
                        data-lpignore="true"
                      />
                    </VisuallyHiddenField>

                    <div className="col-span-12 mb-5">
                      <ReCAPTCHA
                        sitekey="6Lc5iyYsAAAAALRhTMg-dPh0ULO_UbOcdSBVxJA3"
                        onChange={(token) => {
                          setFieldValue('recaptchaToken', token || '');
                        }}
                        onExpired={() => {
                          setFieldValue('recaptchaToken', '');
                        }}
                      />

                      {errors.recaptchaToken && (
                        <p className="mt-2 text-sm text-red-500">{errors.recaptchaToken}</p>
                      )}
                    </div>

                    <div className="col-span-12 mb-4">
                      <Button
                        title={isSending ? 'Sending...' : 'Get My Free Appraisal'}
                        className={`w-full ${!(isValid && dirty) || isSending ? 'pointer-events-none opacity-50' : ''}`}
                        type="button"
                        onClick={() => handleSubmit()}
                      />
                      <span
                        style={{
                          color: '#12203A',
                          fontFamily: 'Untitled Sans',
                          fontSize: '14px',
                          fontWeight: '400',
                          lineHeight: '24px',
                          marginTop: '2px',
                          display: 'block',
                        }}
                      >
                        We typically respond within 2 hours.
                      </span>
                    </div>

                    <div className="contact-btns desktop-contact-btns col-span-12 mb-5">
                      <a href="tel:+442038650611" className="contact-btn">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_call)">
                            <path d="M15.3335 7.33304C15.1567 7.33304 14.9872 7.2628 14.8621 7.13778C14.7371 7.01276 14.6669 6.84319 14.6669 6.66638C14.6655 5.25232 14.1031 3.89659 13.1032 2.8967C12.1033 1.89681 10.7476 1.33445 9.33353 1.33304C9.15672 1.33304 8.98715 1.2628 8.86213 1.13778C8.7371 1.01276 8.66686 0.843186 8.66686 0.666375C8.66686 0.489564 8.7371 0.319995 8.86213 0.19497C8.98715 0.0699462 9.15672 -0.000291733 9.33353 -0.000291733C11.101 0.00164933 12.7956 0.704652 14.0454 1.95447C15.2953 3.2043 15.9983 4.89886 16.0002 6.66638C16.0002 6.84319 15.93 7.01276 15.8049 7.13778C15.6799 7.2628 15.5103 7.33304 15.3335 7.33304ZM13.3335 6.66638C13.3335 5.60551 12.9121 4.58809 12.162 3.83795C11.4118 3.0878 10.3944 2.66637 9.33353 2.66637C9.15672 2.66637 8.98715 2.73661 8.86213 2.86164C8.7371 2.98666 8.66686 3.15623 8.66686 3.33304C8.66686 3.50985 8.7371 3.67942 8.86213 3.80445C8.98715 3.92947 9.15672 3.99971 9.33353 3.99971C10.0408 3.99971 10.7191 4.28066 11.2191 4.78076C11.7192 5.28085 12.0002 5.95913 12.0002 6.66638C12.0002 6.84319 12.0704 7.01276 12.1955 7.13778C12.3205 7.2628 12.4901 7.33304 12.6669 7.33304C12.8437 7.33304 13.0132 7.2628 13.1383 7.13778C13.2633 7.01276 13.3335 6.84319 13.3335 6.66638ZM14.7889 14.7757L15.3955 14.0764C15.7817 13.689 15.9985 13.1643 15.9985 12.6174C15.9985 12.0704 15.7817 11.5458 15.3955 11.1584C15.3749 11.1377 13.7709 9.90371 13.7709 9.90371C13.3859 9.53726 12.8746 9.33321 12.3431 9.33396C11.8116 9.3347 11.3008 9.54018 10.9169 9.90771L9.6462 10.9784C8.60898 10.5491 7.66679 9.91916 6.87372 9.12472C6.08065 8.33028 5.45234 7.387 5.02486 6.34904L6.09153 5.08238C6.45934 4.69849 6.66506 4.18763 6.66593 3.65598C6.6668 3.12433 6.46275 2.61279 6.0962 2.22771C6.0962 2.22771 4.86086 0.625708 4.8402 0.605042C4.45983 0.222207 3.94412 0.00438961 3.40448 -0.00134854C2.86485 -0.0070867 2.34462 0.199715 1.9562 0.574375L1.18953 1.24104C-3.3398 6.49571 6.41353 16.1737 11.8415 15.9997C12.3897 16.0029 12.9329 15.8962 13.4391 15.686C13.9453 15.4758 14.4042 15.1662 14.7889 14.7757V14.7757Z" fill="#FFFDF9" />
                          </g>
                          <defs>
                            <clipPath id="clip0_call">
                              <rect width="16" height="16" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                        <span className="contact-btn-text">Call Us</span>
                      </a>

                      
                        <a href="mailto:info@cityrelay.com?subject=Property%20Enquiry&body=Hi,%20I%20would%20like%20more%20information."
                        className="contact-btn"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_email)">
                            <path d="M15.9693 3.69458L10.3573 9.30658C9.73158 9.93075 8.88383 10.2813 8 10.2813C7.11617 10.2813 6.26842 9.93075 5.64267 9.30658L0.0306667 3.69458C0.0213333 3.79991 0 3.99991V11.9999C0.00105857 12.8836 0.352588 13.7309 0.97748 14.3558C1.60237 14.9807 2.4496 15.3322 3.33333 15.3332H12.6667C13.5504 15.3322 14.3976 14.9807 15.0225 14.3558C15.6474 13.7309 15.9989 12.8836 16 11.9999V3.99991C16 3.89525 15.9787 3.79991 15.9693 3.69458Z" fill="#FFFDF9" />
                            <path d="M9.41476 8.3639L15.5041 2.2739C15.2091 1.78478 14.7931 1.37992 14.2961 1.09839C13.7991 0.816847 13.2379 0.668125 12.6668 0.666565H3.33343C2.76224 0.668125 2.20109 0.816847 1.70411 1.09839C1.20713 1.37992 0.791079 1.78478 0.496094 2.2739L6.58543 8.3639C6.96114 8.73811 7.46982 8.94821 8.00009 8.94821C8.53037 8.94821 9.03905 8.73811 9.41476 8.3639Z" fill="#FFFDF9" />
                          </g>
                          <defs>
                            <clipPath id="clip0_email">
                              <rect width="16" height="16" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                        <span className="contact-btn-text">Email Us</span>
                      </a>
                    </div>
                  </div>
                )}
              </Formik>
            ) : (
              <div className="max-w-lg mx-auto text-center">
                <StaticImage
                  src={'../../assets/images/form-success.png'}
                  objectFit="contain"
                  objectPosition="center"
                  alt="CityRelay"
                  className="w-40 mb-8"
                />
                <h2 className="mb-4 text-h3">Thanks!</h2>
                <p className="max-w-xs mx-auto">
                  We've received your request — one of our experts will contact you within 2 hours.
                </p>
              </div>
            )}
          </div>
        </div>
      </FadeInWhenVisible>
    </>
  );
}